import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useStaticQuery, graphql } from 'gatsby'
import Img, { FluidObject } from 'gatsby-image'

import { Container } from 'components/Container'
import { backgroundColors, colors } from 'styles/colors'
import { displayWidth } from 'styles/width'
import { mobileAfterBorder } from 'styles/mobileAfterBorder'
import { Title } from 'components/TitleComponent'
import { getDataByLanguage } from 'utils/getDataByLanguage'
import { LocalizedLinkAnchor } from 'i18n/LocalizedLink'
import { Button } from 'components/Button'
import { JumpingArrow } from 'components/JumpingArrow'
import { indent } from 'styles/indent'
import { sendEvent } from 'tracking'
import { imagesDataProp } from 'types/interfaces'
import { getImageByImageName } from 'utils/getImageByImageName'

const SelectionOfPaintWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    background-color: ${backgroundColors.services};
    position: relative;
    border-bottom: 1px solid ${colors.dark};
    ${mobileAfterBorder}
`

const SubTitle = styled.h3`
    font-weight: normal;
    font-size: 16px;
    line-height: 26px;
    text-align: center;
    letter-spacing: 0.4px;
    color: ${colors.dark};
    margin: 20px auto;
    @media (min-width: ${displayWidth.tablet}) {
        margin: 0 ${indent.heroColumnTablet} 48px;
        text-align: left;
    }
    @media (min-width: ${displayWidth.desktop}) {
        margin: 0 ${indent.heroColumnDesktop} 48px;
    }
`

const HeroColumn = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 0 32px 32px;
    align-items: center;
    border-top: 1px solid ${colors.dark};
    border-bottom: 1px solid ${colors.dark};
    @media (min-width: ${displayWidth.tablet}) {
        border-top: none;
        border-bottom: none;
        padding: 0px 0 32px;
        border-right: 1px solid ${colors.dark};
    }
`
const ImgStyled = styled(Img)<{ fluid: FluidObject }>`
    height: 70vw;
    max-height: 100%;
    @media (min-width: ${displayWidth.tablet}) {
        width: calc((100vw - 160px) * 0.6666);
        height: 100%;
    }
    @media (min-width: ${displayWidth.desktop}) {
        width: 793px;
    }
`
const ImgWrapper = styled.div`
    padding: 0 16px;
    @media (min-width: ${displayWidth.tablet}) {
        padding: 0;
    }
`
const LocalizedLinkStyled = styled(LocalizedLinkAnchor)`
    text-decoration: none;
`
const ButtonStyled = styled(Button)`
    width: 264px;
    margin: 0 auto 50px;
    z-index: 3;
    @media (max-width: 330px) {
        width: 250px;
    }
    @media (min-width: ${displayWidth.tablet}) {
        width: 220px;
    }
    @media (min-width: ${displayWidth.desktop}) {
        width: 264px;
    }
`
const TitleS = styled(Title)`
    @media (min-width: ${displayWidth.tablet}) {
        display: block;
        text-align: center;
        margin-right: 40px;
        line-height: 44px;
        font-size: 28px;
    }
    @media (min-width: ${displayWidth.desktop}) {
        font-size: 34px;
    }
`
export const SelectionOfPaints = ({
    imagesData,
    setAdvantagesServicesIsVisible,
}: {
    imagesData: imagesDataProp
    setAdvantagesServicesIsVisible: (arg: boolean) => void
}) => {
    const { i18n } = useTranslation()
    const data = useStaticQuery(graphql`
        query {
            allSelectionOfPaintYaml {
                edges {
                    node {
                        title
                        subTitle
                        buttonText
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
    `)
    const selectionOfPaintYaml = getDataByLanguage(
        data.allSelectionOfPaintYaml,
        i18n.language
    )
    const { title, subTitle, buttonText } = selectionOfPaintYaml

    const image = getImageByImageName(imagesData, 'colorsAndTextur.webp')
    return (
        <SelectionOfPaintWrapper>
            <Container columns={'1fr'} tabletColumns={'1fr 2fr'}>
                <HeroColumn>
                    <TitleS>{title}</TitleS>
                    <SubTitle>{subTitle}</SubTitle>
                    <LocalizedLinkStyled
                        to={'/style/'}
                        onClick={() => {
                            setAdvantagesServicesIsVisible(true)
                            sendEvent('Click', {
                                eventCategory: 'ShowMoreButton',
                                placement: 'ColorSelection',
                                target: 'AdvantagesServices',
                            })
                        }}
                    >
                        <ButtonStyled>{buttonText}</ButtonStyled>
                    </LocalizedLinkStyled>
                    <JumpingArrow />
                </HeroColumn>
                <ImgWrapper>
                    <ImgStyled
                        fluid={image.fluid}
                        loading="eager"
                        alt="room with colors image"
                        title="room with colors image"
                    />
                </ImgWrapper>
            </Container>
        </SelectionOfPaintWrapper>
    )
}
